import { create } from "zustand"
import type { Allegation } from "@/types"

interface AnalysisState {
  allegations: Allegation[]
  summary: string
  scnSummary: string | undefined
  replySummary: string | undefined
  legalProvisions: string[]
  confirmedCount: number
  partialCount: number
  droppedCount: number

  setAnalysisData: (data: {
    allegations?: Allegation[]
    summary?: string
    scnSummary?: string
    replySummary?: string
    legalProvisions?: string[]
    findings_summary?: Allegation[]
  }) => void
  reset: () => void
}

function computeCounts(allegations: Allegation[]) {
  return {
    confirmedCount: allegations.filter((a) => a.status === "confirmed").length,
    partialCount: allegations.filter((a) => a.status === "partial").length,
    droppedCount: allegations.filter((a) => a.status === "dropped").length,
  }
}

const initialState = {
  allegations: [],
  summary: "",
  scnSummary: undefined,
  replySummary: undefined,
  legalProvisions: [],
  confirmedCount: 0,
  partialCount: 0,
  droppedCount: 0,
}

export const useAnalysisStore = create<AnalysisState>((set) => ({
  ...initialState,

  setAnalysisData: (data) =>
    set((prev) => {
      const allegations =
        (data.findings_summary ?? data.allegations ?? prev.allegations) as Allegation[]
      // Bail when nothing actually changed. The findings restore/sync effects
      // re-run on load and re-cache the SAME findings; without this bail a new
      // `allegations` array was minted each time, re-rendering the workspace
      // and re-mounting the 60-row Analysis pane on repeat — the freeze. A
      // shallow per-row compare is far cheaper than that churn.
      const summary = data.summary ?? prev.summary
      const scnSummary = data.scnSummary ?? prev.scnSummary
      const replySummary = data.replySummary ?? prev.replySummary
      const legalProvisions = data.legalProvisions ?? prev.legalProvisions
      const sameAllegations =
        allegations === prev.allegations ||
        (allegations.length === prev.allegations.length &&
          allegations.every((a, i) => {
            const b = prev.allegations[i]
            return (
              a.id === b.id &&
              a.status === b.status &&
              a.amount === b.amount &&
              a.allegation === b.allegation &&
              a.section === b.section
            )
          }))
      if (
        sameAllegations &&
        summary === prev.summary &&
        scnSummary === prev.scnSummary &&
        replySummary === prev.replySummary &&
        legalProvisions === prev.legalProvisions
      ) {
        return prev
      }
      return {
        allegations: sameAllegations ? prev.allegations : allegations,
        summary,
        scnSummary,
        replySummary,
        legalProvisions,
        ...computeCounts(sameAllegations ? prev.allegations : allegations),
      }
    }),

  reset: () => set(initialState),
}))
